import { createContext, useContext, useReducer } from 'react';
import { ThemeProvider } from './ThemeContext';
import config from '../config/config';

// Initial state
const initialState = {
  user: null,
  loading: false,
  error: null,
  language: config.defaultLanguage,
  services: [],
  works: [],
  notification: null
};

// Action types
export const ActionTypes = {
  SET_USER: 'SET_USER',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_LANGUAGE: 'SET_LANGUAGE',
  SET_SERVICES: 'SET_SERVICES',
  SET_WORKS: 'SET_WORKS',
  SET_NOTIFICATION: 'SET_NOTIFICATION'
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_USER:
      return { ...state, user: action.payload };
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload };
    case ActionTypes.SET_LANGUAGE:
      return { ...state, language: action.payload };
    case ActionTypes.SET_SERVICES:
      return { ...state, services: action.payload };
    case ActionTypes.SET_WORKS:
      return { ...state, works: action.payload };
    case ActionTypes.SET_NOTIFICATION:
      return { ...state, notification: action.payload };
    default:
      return state;
  }
};

// Context
export const AppContext = createContext(null);

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      <ThemeProvider>
        {children}
      </ThemeProvider>
    </AppContext.Provider>
  );
};

// Custom hook for global state management
export const useGlobalState = () => {
  const { state, dispatch } = useAppContext();

  const actions = {
    setLoading: (loading) => dispatch({ type: ActionTypes.SET_LOADING, payload: loading }),
    setError: (error) => dispatch({ type: ActionTypes.SET_ERROR, payload: error }),
    setServices: (services) => dispatch({ type: ActionTypes.SET_SERVICES, payload: services }),
    setWorks: (works) => dispatch({ type: ActionTypes.SET_WORKS, payload: works }),
    setNotification: (notification) => dispatch({ type: ActionTypes.SET_NOTIFICATION, payload: notification }),
    clearNotification: () => dispatch({ type: ActionTypes.SET_NOTIFICATION, payload: null }),
  };

  return {
    ...state,
    ...actions
  };
};

// Custom hook to use the app context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
